import { MeetingCubit } from './MeetingCubit'
import { makeObservable, action, observable, reaction, runInAction } from 'mobx'
import { PreviewDataFetcher } from './meeting/preview/PreviewDataFetcher'
import type { MeetingDataFetcher } from './meeting/MeetingDataFetcher'
import type { SlideTimerController } from './meeting/SlideTimerController'
import { PreviewSlideTimerController } from './meeting/preview/PreviewSlideTimerController'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { BreakoutUser } from '../models/BreakoutUser'
import { PreviewQuestion } from './meeting/preview/PreviewQuestion'
import { RoomStateAnswer } from '../models/RoomStateAnswer'
import { PublicUser } from '../models/PublicUser'
import { doc } from 'firebase/firestore'
import type { RoomState } from '../models/RoomState'
import { type SlideQuestion } from '../models/SlideQuestion'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { getTalkTrackAudio } from '../firestore/SlideQuestion'
import { noTryAsync } from '../util'
import { captureException } from '@sentry/core'
import { isSafari } from '../util/DeviceManager'
import type { SlideDeck } from '../models/SlideDeck'
import type { SlideDeckExhibit } from '../models/SlideDeckExhibit'
import type { SlideModel } from '../models/SlideModel'
import { SeededPreviewDataFetcher } from './meeting/preview/SeededPreviewDataFetcher'

const ttVol = -0.25

export type SlideDeckPreviewData = {
  slideDeck: SlideDeck
  slideDeckQuestions: SlideQuestion[]
  slideDeckExhibits: SlideDeckExhibit[]
  slideDeckSlides: SlideModel[]
}

export class PreviewMeetingCubit extends MeetingCubit {
  isPreview: boolean = true
  _activeSlide: number = 0
  _activeSlideChangedAt: number = Date.now()
  _activeExhibitId: string | null = null
  _slideDeckId: string | undefined
  private _seedUsers: PublicUser[] | undefined
  /**
   * if a slide deck is passed into the constructor, we use this to store it and all of its subcollections
   * so that we don't have to fetch any of this data from firestore
   */
  _slideDeckPreviewData: SlideDeckPreviewData | undefined

  // AudioContext/Analyzer/AbortController live for the lifecycle of the cubit
  private _audioContext = new AudioContext()
  private _talkTrackAnalyzer = this._audioContext.createAnalyser()
  private _talkTrackRequestAbortController: AbortController | null = null

  // audio element and ended listener are created when the talk track is played
  // and destroyed when the talk track ends or is killed (slide change, cubit close, etc)
  private _talkTrack: {
    audio: HTMLAudioElement
    gainNode: GainNode
    kill: () => void
  } | null = null

  talkTrackMuted = observable.box(false)
  _previewSectionId?: string
  _previewAssignmentId?: string

  // we want either slideDeckId or roomState ID
  // not both
  constructor({
    currentUser,
    repository,
    slideDeckPreviewData,
    slideDeckId,
    roomId,
    sectionId,
    assignmentId,
    seedData,
    enableTalkTrack,
  }:
    | {
        currentUser: BreakoutUser
        repository: FirebaseRepository
        slideDeckPreviewData?: never
        slideDeckId: string
        roomId?: never
        /**
         * Optional section/assignment context for student access.
         * Instructors/TAs/admins can preview with just slideDeckId.
         * Students MUST provide these for the API to authorize access.
         */
        sectionId?: string
        assignmentId?: string
        /**
         * additional data to seed the cubit if need be,
         * this is used for the meeting results dialog as the results cubit has
         * a dependency on meeting cubit, so we must seed the data
         */
        seedData?: {
          roomStateAnswersForGroup: RoomStateAnswer[]
          roomStateAnswersPerUser: Map<string, RoomStateAnswer[]>
          roomState: RoomState
          users: PublicUser[]
        }
        enableTalkTrack: boolean
      }
    | {
        currentUser: BreakoutUser
        repository: FirebaseRepository
        slideDeckPreviewData?: never
        /**
         * if room ID is supplied the data fetcher will init the stream to fetch all relevant slide deck data
         */
        roomId: string
        slideDeckId?: never
        sectionId?: never
        assignmentId?: never
        seedData?: never
        enableTalkTrack: boolean
      }
    | {
        currentUser: BreakoutUser
        repository: FirebaseRepository
        slideDeckPreviewData: SlideDeckPreviewData
        roomId?: never
        slideDeckId?: never
        sectionId?: never
        assignmentId?: never
        seedData?: {
          roomStateAnswersForGroup: RoomStateAnswer[]
          roomStateAnswersPerUser: Map<string, RoomStateAnswer[]>
          roomState: RoomState
          users: PublicUser[]
        }
        enableTalkTrack: boolean
      }) {
    super(currentUser, repository, roomId || '', { slideDeckPreviewData })
    this.useMockResults = roomId === undefined && seedData === undefined
    this._slideDeckId = slideDeckId
    this._previewSectionId = sectionId
    this._previewAssignmentId = assignmentId

    if (slideDeckPreviewData) {
      this._slideDeckPreviewData = slideDeckPreviewData
    }
    this.chatController = undefined

    this.sidebarTab = 'agenda'

    this.roomState.replaceData({
      ...this.roomState.data,
      userIds: [currentUser.uid],
    })

    this.roomStateSummary.replaceData({
      ...this.roomStateSummary.data,
      roomId: this.roomId,
      updatedAt: new Date(),
      summary: `### Executive Summary\n- **John Doe**:\n  - Discussed the
     functionality and user interface of the platform.\n  - Raised questions
     about the AI transcript functionality.\n  - Provided feedback on the
     interactive elements of the platform.\n- **Jane Austin**:\n  -
     Clarified the role of the group leader and the different options available.
     \n  - Discussed the purpose of the platform, highlighting its potential for
     improving education.\n  - Addressed technical issues and explained the
     platform's features.\n  - Gave feedback on the case study content and its
     suitability for different audiences.\n### Conclusion\nThe discussion
     primarily revolved around the functionality and potential of the platform,
     with John Doe providing user feedback and Jane Austin explaining
     the features and purpose of the platform. The topic of AI transcript
     functionality led to a significant discussion. The students also discussed
     the content of the case studies, with Jane Austin providing insights
     into their suitability for different audiences. The conversation
     highlighted the platform's potential for improving education through
     interactive group discussions.`,
    })

    this.roomStateAnswersPerUser.set(
      currentUser.uid,
      RoomStateAnswer.emptyCollection(repository)
    )

    // This is needed for the quiz slide to work
    const answersCol = RoomStateAnswer.emptyCollection(repository)
    answersCol.replaceModels([])
    this.roomStateAnswersPerUser.set(currentUser.uid, answersCol)

    if (seedData) {
      this.roomState.replaceModel(seedData.roomState)
      this.roomStateAnswersForGroup.replaceModels(
        seedData.roomStateAnswersForGroup
      )
      for (const [userId, answers] of seedData.roomStateAnswersPerUser) {
        const answersCol = RoomStateAnswer.emptyCollection(repository)
        answersCol.replaceModels(answers)
        this.roomStateAnswersPerUser.set(userId, answersCol)
      }
      this._seedUsers = seedData.users
    }

    makeObservable(this, {
      showExhibitPreview: action,
      _activeSlide: observable,
      _activeSlideChangedAt: observable,
      _activeExhibitId: observable,
    })

    // unless explicitly enabled, don't start the talk track listeners
    if (!enableTalkTrack) return

    // when the active slide or questions changes, check if there is a talk track
    // if so, play the talk track
    this.disposers.push(
      reaction(
        () => ({
          slide: this.currentSlide,
          questions: this.slideDeckQuestions.models,
        }),
        ({ questions, slide }) => {
          // abort any previous requests
          this._talkTrackRequestAbortController?.abort()

          // use kill function if available, else set speaking false
          this._talkTrack
            ? this._talkTrack.kill()
            : this.setIsModeratorSpeaking(false)

          if (!slide || !questions.length) return

          const talkTrackForSlide = questions.find((q) => {
            return (
              q.data.slideId &&
              q.questionType === SlideQuestionType.talkTrack &&
              q.data.slideId === slide.id
            )
          })
          if (!talkTrackForSlide) return

          // if starting a new talk track, create a new abort controller
          this._talkTrackRequestAbortController = new AbortController()
          this.initTalkTrackForSlide(talkTrackForSlide)
        },
        { fireImmediately: true }
      )
    )

    this.disposers.push(() => {
      this._talkTrack?.kill()
      this._audioContext.close()
    })

    // these values come from the livekit implementation for the
    // track analyzer
    this._talkTrackAnalyzer.fftSize = 32
    this._talkTrackAnalyzer.smoothingTimeConstant = 0
    this._talkTrackAnalyzer.minDecibels = -100
    this._talkTrackAnalyzer.maxDecibels = -80
  }

  constructDataFetcher(hasSlideDeckPreviewData?: boolean): MeetingDataFetcher {
    if (hasSlideDeckPreviewData) {
      return new SeededPreviewDataFetcher(this)
    } else {
      return new PreviewDataFetcher(this)
    }
  }

  constructSlideTimerController(): SlideTimerController {
    return new PreviewSlideTimerController(this)
  }

  showExhibitPreview(exhibitId: string | null) {
    this._activeExhibitId = exhibitId
  }

  roomStateRef() {
    if (this instanceof PreviewMeetingCubit)
      throw new Error('Method not implemented')
    return super.roomStateRef()
  }

  // For preview, disable the slide closing check - we want to be able to
  // switch slides even if the closing animation is playing
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSlideIsClosing(value: boolean) {
    return
  }

  setSlide(index: number) {
    console.log(`[preview_meeting_cubit] setSlide: ${index}`) // eslint-disable-line no-console
    console.log('Current active slide:', this._activeSlide) // eslint-disable-line no-console
    if (this.slideChangeHook) this.slideChangeHook(index)

    // cleanup the hook after running so it only runs once
    this.slideChangeHook = null

    runInAction(() => {
      const newSlideIsDifferent = this._activeSlide !== index

      if (!newSlideIsDifferent) return

      this._activeSlide = index
      this._activeSlideChangedAt = Date.now()
      this._activeExhibitId = null

      const data = this.roomState.data
      this.roomState.replaceData({
        ...data,
        activeSlide: index,
      })

      this.moderationTasksController.setShowDisplay(false)

      this.resetSlideTimer()
    })
  }

  toggleMuteTalkTrack(mute: boolean) {
    if (!this._talkTrack) return
    const muteAudio = mute ?? !this.talkTrackMuted.get()
    this._talkTrack.gainNode.gain.value = muteAudio ? -1 : ttVol
    this.talkTrackMuted.set(muteAudio)
  }

  shareExhibit(exhibitId: string): void {
    this._activeExhibitId = exhibitId
  }

  stopSharingExhibit(): void {
    this._activeExhibitId = null
  }

  // mirrored from livekit implementation for track volume
  getModeratorVolume() {
    const bufferLength = this._talkTrackAnalyzer.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)
    this._talkTrackAnalyzer.getByteFrequencyData(dataArray)

    const sum = dataArray.reduce((sum, value) => sum + value * value, 0)

    return Math.sqrt(sum / dataArray.length) / 255
  }

  get activeSlide() {
    return this._activeSlide
  }

  // @computed
  get questions(): PreviewQuestion[] {
    return this.slideDeckQuestions.models.map((model) => {
      return new PreviewQuestion(this, model)
    })
  }

  get activeExhibitId(): string | null {
    return this._activeExhibitId
  }

  get chat() {
    if (this.isPreview) throw new Error('cannot access chat in preview meeting')
    return super.chat
  }

  get users() {
    const emptyRef = doc(this.repository.firestore, 'empty/empty')
    // mock some public users
    if (this.useMockResults) {
      return [
        new PublicUser(this.repository, {
          id: '1',
          data: {
            firstName: 'John',
            lastName: 'Doe',
            imageUrl: 'https://robohash.org/set_set1/bgset_bg1/1',
          },
          ref: emptyRef,
          fromCache: false,
          hasPendingWrites: false,
          exists: true,
        }),
        new PublicUser(this.repository, {
          id: '2',
          data: {
            firstName: 'Jane',
            lastName: 'Austin',
            imageUrl: 'https://robohash.org/set_set1/bgset_bg1/2',
          },
          ref: emptyRef,
          fromCache: false,
          hasPendingWrites: false,
          exists: true,
        }),
        new PublicUser(this.repository, {
          id: '3',
          data: {
            firstName: 'Tim',
            lastName: 'Anderson',
            imageUrl: 'https://robohash.org/set_set1/bgset_bg1/3',
          },
          ref: emptyRef,
          fromCache: false,
          hasPendingWrites: false,
          exists: true,
        }),
      ]
    }
    if (this._seedUsers) {
      return this._seedUsers
    }
    return super.users
  }

  get isSharingExhibit(): boolean {
    return this.activeExhibitId !== null
  }

  async sendFeedback(): Promise<void> {
    throw new Error('Method not implemented')
  }

  get currentUserIsGroupLeader() {
    return true
  }

  get sessionResults(): {
    pollAgreement: number
    quizPerformance: number
  } {
    // return some dummy percentage values
    return {
      pollAgreement: 0.875,
      quizPerformance: 0.74,
    }
  }

  get currentSlideExhibits() {
    const slide = this.currentSlide
    if (!slide) return []

    return this.meetingExhibits.sort((a, b) => {
      return a.data.exhibitName.localeCompare(b.data.exhibitName)
    })
  }

  private async initTalkTrackForSlide(question: SlideQuestion) {
    if (
      question.questionType !== SlideQuestionType.talkTrack ||
      !this._slideDeckId
    ) {
      return
    }
    // show moderator prior to the audio starting
    this.setIsModeratorSpeaking(true)

    const audioRequest = getTalkTrackAudio(this.repository, {
      questionId: question.id,
      slideDeckId: this._slideDeckId,
      noPlaceholderAudio: true,
      // pass signal so we can terminate the request on slide change
      abortSignal: this._talkTrackRequestAbortController?.signal,
    })

    const [data, err] = await noTryAsync(() => audioRequest)

    if (err) {
      this.setIsModeratorSpeaking(false)

      // don't show/capture error if the request was aborted
      if (err.name === 'AbortError')
        return this.debug('Talk track request aborted')

      console.error(err)
      return captureException(err)
    }

    // if cubit has disposed, back out
    if (this.isDisposed) return

    const { isPlaceholder, audio } = data

    // if placeholder audio, back out
    if (isPlaceholder) return this.setIsModeratorSpeaking(false)

    // attach the analyzer
    const source = this._audioContext.createMediaElementSource(audio)
    const gainNode = this._audioContext.createGain()

    source.connect(this._talkTrackAnalyzer)
    source.connect(gainNode).connect(this._talkTrackAnalyzer)

    this._talkTrackAnalyzer.connect(this._audioContext.destination)

    // create kill func, to be called onended or manually
    const killTalkTrack = () => {
      audio.pause()

      source.disconnect(this._talkTrackAnalyzer)
      source.disconnect()

      gainNode.disconnect(this._talkTrackAnalyzer)
      gainNode.disconnect()

      this._talkTrackAnalyzer.disconnect(this._audioContext.destination)

      // Remove the event listener before setting it to null
      audio.removeEventListener('ended', killTalkTrack)
      audio.remove()

      this.setIsModeratorSpeaking(false)

      this._talkTrack = null
    }

    audio.addEventListener('ended', killTalkTrack)

    this._talkTrack = {
      audio,
      gainNode,
      kill: killTalkTrack,
    }

    // safari briefly plays audio through the native audio path even when using Web Audio API.
    // setting volume 0 ensures there's no overlap (double playback) at the start
    if (isSafari()) audio.volume = 0

    const muted = this.talkTrackMuted.get()
    gainNode.gain.value = muted ? -1 : ttVol

    // start the audio playback
    // set true on play in case abort call set false after init
    const [, playErr] = await noTryAsync(() => audio.play())
    if (playErr) {
      killTalkTrack()
      this.debug(`Error playing audio: ${playErr.message}`)
      captureException(playErr)
    }
    this.setIsModeratorSpeaking(!playErr)
  }
}
